import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react'

interface AuthContextValue {
  accessToken: string | null
  setAccessToken: (token: string | null) => void
  // null = role not resolved yet this session (checkLogin hasn't returned)
  group: string[] | null
  setGroup: (group: string[] | null) => void
  isAdmin: boolean
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [accessToken, setAccessTokenState] = useState<string | null>(() => {
    return localStorage.getItem('access_token')
  })
  const [group, setGroup] = useState<string[] | null>(null)

  // Persist synchronously so localStorage (the source of truth for the API
  // client) is updated before any effect reads it — avoids a stale-token race
  // on first login. Memoized so its identity is stable across renders: it's
  // an effect dependency in App.tsx, and an unstable reference there
  // re-triggers checkLogin() on every group update, looping forever.
  const setAccessToken = useCallback((token: string | null) => {
    if (token) {
      localStorage.setItem('access_token', token)
    } else {
      localStorage.removeItem('access_token')
    }
    setAccessTokenState(token)
  }, [])

  const isAdmin = group?.includes('admin') ?? false

  return (
    <AuthContext value={{ accessToken, setAccessToken, group, setGroup, isAdmin }}>
      {children}
    </AuthContext>
  )
}

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
